import { RefObject, useEffect } from "react";

export type DismissReason = "outside" | "escape";

/**
 * Closes a popover (menu, dropdown, panel) when the user clicks/taps outside it
 * or presses Escape. Only listens while `open` is true.
 */
const useDismiss = (
  ref: RefObject<HTMLElement>,
  open: boolean,
  onDismiss: (reason: DismissReason) => void
) => {
  useEffect(() => {
    if (!open) return;

    const handlePointer = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onDismiss("outside");
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss("escape");
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [ref, open, onDismiss]);
};

export default useDismiss;
